// שאל את ה-CRM — the chat page. Posts each question to /AiChat/Ask and renders the answer with
// what it cost. The answer is shown as text: HTML is escaped and only **bold** is honoured, so a
// model reply (or a customer remark quoted in one) can never inject markup into the page.

interface Tool {
  name: string;
  arguments: string;
  failed: boolean;
}

interface Usage {
  todayQuestions: number;
  todayUsd: number;
  monthQuestions: number;
  monthUsd: number;
  conversationUsd: number;
  dailyLimit: number;
}

interface Reply {
  conversationId: string;
  answer: string;
  error?: string | null;
  model: string;
  tools: Tool[];
  tokens: { input: number; cached: number; output: number };
  costUsd: number;
  costIls?: number | null;
  seconds: number;
  usage: Usage;
}

const esc = (s: unknown): string =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/** Escaped text with **bold** kept and line breaks preserved (the container is pre-wrap). */
const renderAnswer = (s: string): string => esc(s).replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>");

/** Dollars with enough digits to see a fraction of a cent. */
const usd = (v: number): string => "$" + (v < 1 ? v.toFixed(4) : v.toFixed(2));
const n = (v: number): string => v.toLocaleString("he-IL");

export function initAiChat(): void {
  const root = document.getElementById("ai-chat");
  if (!root) return;
  const log = root.querySelector<HTMLElement>("[data-ai-log]")!;
  const form = root.querySelector<HTMLFormElement>("[data-ai-form]")!;
  const input = form.querySelector<HTMLTextAreaElement>("textarea")!;
  const send = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
  const token = form.querySelector<HTMLInputElement>("input[name=__RequestVerificationToken]")?.value ?? "";
  const ils = parseFloat(root.dataset.ils || "") || 0;
  let conversationId = "";
  let busy = false;

  const money = (v: number): string => (ils > 0 ? `${usd(v)} (≈ ₪${(v * ils).toFixed(v * ils < 1 ? 3 : 2)})` : usd(v));
  // Isolated left-to-right, or "(≈ ₪0.003)" comes out mirrored inside the Hebrew line.
  const moneyHtml = (v: number): string => `<bdi dir="ltr">${esc(money(v))}</bdi>`;

  function showUsage(u: Usage): void {
    const limit = u.dailyLimit > 0 ? ` מתוך ${u.dailyLimit}` : "";
    root!.querySelector("[data-usage=today]")!.innerHTML = `${u.todayQuestions}${esc(limit)} שאלות · ${moneyHtml(u.todayUsd)}`;
    root!.querySelector("[data-usage=month]")!.innerHTML = `${u.monthQuestions} שאלות · ${moneyHtml(u.monthUsd)}`;
    const conv = root!.querySelector<HTMLElement>("[data-usage-conv-wrap]")!;
    conv.classList.toggle("hidden", !conversationId);
    root!.querySelector("[data-usage=conversation]")!.innerHTML = moneyHtml(u.conversationUsd);
  }

  showUsage({
    todayQuestions: +(root.dataset.todayQ || 0), todayUsd: +(root.dataset.todayUsd || 0),
    monthQuestions: +(root.dataset.monthQ || 0), monthUsd: +(root.dataset.monthUsd || 0),
    conversationUsd: 0, dailyLimit: +(root.dataset.limit || 0),
  });

  function add(html: string): HTMLElement {
    root!.querySelector("[data-ai-empty]")?.remove();
    const el = document.createElement("div");
    el.innerHTML = html;
    const node = el.firstElementChild as HTMLElement;
    log.appendChild(node);
    log.scrollTop = log.scrollHeight;
    return node;
  }

  function question(text: string): void {
    add(`<div class="flex justify-start"><div class="max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-ss-sm bg-brand-500 px-4 py-2.5 text-sm text-white">${esc(text)}</div></div>`);
  }

  function answer(r: Reply): void {
    const tools = r.tools.length
      ? `<details class="mt-2 text-xs text-gray-500"><summary class="cursor-pointer select-none">${r.tools.length === 1 ? "קריאה אחת לכלי" : `${r.tools.length} קריאות לכלים`}</summary>
           <ul class="mt-1 space-y-0.5 font-mono" dir="ltr">${r.tools
             .map((t) => `<li class="${t.failed ? "text-error-600" : ""}">${esc(t.name)} ${esc(t.arguments)}</li>`)
             .join("")}</ul></details>`
      : "";
    const meta = `<div class="mt-2 flex flex-wrap gap-x-3 gap-y-1 border-t border-line pt-2 text-xs text-gray-500">
        <span>עלות: <b class="text-gray-700">${moneyHtml(r.costUsd)}</b></span>
        <span>טוקנים: ${n(r.tokens.input)} קלט (${n(r.tokens.cached)} ממטמון) · ${n(r.tokens.output)} פלט</span>
        <span>${r.seconds} שניות · ${esc(r.model)}</span></div>`;
    const body = r.error
      ? `<div class="whitespace-pre-wrap text-sm text-error-700">${esc(r.error)}</div>`
      : `<div class="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">${renderAnswer(r.answer)}</div>`;
    add(`<div class="flex justify-end"><div class="max-w-[90%] rounded-2xl rounded-se-sm bg-white px-4 py-3 ring-1 ring-line">${body}${meta}${tools}</div></div>`);
  }

  async function ask(text: string): Promise<void> {
    if (busy || !text.trim()) return;
    busy = true;
    send.disabled = true;
    input.value = "";
    question(text);
    const wait = add(`<div class="flex justify-end"><div class="rounded-2xl bg-white px-4 py-3 text-sm text-gray-500 ring-1 ring-line">בודק בנתונים…</div></div>`);
    try {
      const res = await fetch("/AiChat/Ask", {
        method: "POST",
        headers: { "Content-Type": "application/json", RequestVerificationToken: token, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ conversationId, question: text }),
      });
      const data = await res.json().catch(() => null);
      wait.remove();
      if (!res.ok || !data) {
        add(`<div class="flex justify-end"><div class="rounded-2xl bg-error-50 px-4 py-3 text-sm text-error-700">${esc(data?.error || "השאלה לא נשלחה (" + res.status + ")")}</div></div>`);
        return;
      }
      conversationId = data.conversationId;
      answer(data as Reply);
      showUsage(data.usage);
    } catch {
      wait.remove();
      add(`<div class="flex justify-end"><div class="rounded-2xl bg-error-50 px-4 py-3 text-sm text-error-700">אין חיבור לשרת. נסה שוב.</div></div>`);
    } finally {
      busy = false;
      send.disabled = false;
      input.focus();
    }
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    void ask(input.value);
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void ask(input.value);
    }
  });
  root.querySelectorAll<HTMLButtonElement>("[data-ai-example]").forEach((b) =>
    b.addEventListener("click", () => void ask(b.textContent || "")));
  root.querySelector("[data-ai-new]")!.addEventListener("click", () => {
    conversationId = "";
    log.innerHTML = "";
    root!.querySelector<HTMLElement>("[data-usage-conv-wrap]")!.classList.add("hidden");
    input.focus();
  });
  input.focus();
}
